import React, { useMemo } from 'react';

import {
    useAggregateGlossaryTermsForWidgetQuery,
    useGetSearchResultsForMultipleQuery,
} from '../../graphql/search.generated';
import { EntityType } from '../../types.generated';
import { colorUtilService, ColumnChart } from '../shared/charts';
import { useTranslation } from '../../i18n/useTranslation';
import { GLOSSARY_TERMS_FILTER_NAME } from '../search/utils/constants';
import { COMMON_ASSET_ENTITY_TYPES, getAggDisplayName, getFacetAggregations } from './utils';
import { ChartWidgetCard } from './ChartWidgetCard';
import { mergeDashboardFilters, useDashboardFilters } from './dashboardFilters';

const TOP_TERMS_LIMIT = 6;
// Upper bound on the number of glossary terms resolved before aggregating their assets.
const GLOSSARY_TERMS_SEARCH_COUNT = 400;

export const EntitiesPerGlossaryTermWidget = () => {
    const { t } = useTranslation();
    const dashboardFilters = useDashboardFilters();

    // API 1: find the glossary terms owned by / falling under the selected owners and/or domains.
    const { data: glossaryTermsData, loading: glossaryTermsLoading } = useGetSearchResultsForMultipleQuery({
        variables: {
            input: {
                types: [EntityType.GlossaryTerm],
                query: '*',
                start: 0,
                count: GLOSSARY_TERMS_SEARCH_COUNT,
                orFilters: mergeDashboardFilters(dashboardFilters),
            },
        },
        fetchPolicy: 'no-cache',
    });

    const termUrns = useMemo(
        () => glossaryTermsData?.searchAcrossEntities?.searchResults?.map((result) => result.entity.urn) ?? [],
        [glossaryTermsData],
    );

    // Memoize the full orFilters object so Apollo doesn't see new variables on every render.
    const glossaryOrFilters = useMemo(
        () => [{ and: [{ field: GLOSSARY_TERMS_FILTER_NAME, values: termUrns }] }],
        [termUrns],
    );

    // API 2: aggregate the assets those terms are attached to.
    const { data: entitiesPerGlossaryTermData, loading: aggregationLoading } = useAggregateGlossaryTermsForWidgetQuery({
        variables: {
            input: {
                query: '*',
                facets: [GLOSSARY_TERMS_FILTER_NAME],
                types: COMMON_ASSET_ENTITY_TYPES,
                orFilters: glossaryOrFilters,
            },
        },
        fetchPolicy: 'no-cache',
        skip: termUrns.length === 0,
    });

    const entitiesPerGlossaryTermLoading = glossaryTermsLoading || aggregationLoading;

    const { categories, series } = useMemo(() => {
        const aggregations = getFacetAggregations(
            entitiesPerGlossaryTermData?.aggregateAcrossEntities?.facets,
            GLOSSARY_TERMS_FILTER_NAME,
        );

        if (aggregations.length === 0) {
            return { categories: [] as string[], series: [] };
        }

        const topTerms = aggregations.sort((a, b) => b.count - a.count).slice(0, TOP_TERMS_LIMIT);

        return {
            categories: topTerms.map((agg) => getAggDisplayName(agg, t('Unknown'))),
            series: [
                {
                    name: t('Total Entities'),
                    color: colorUtilService.domainsColorMap.entities,
                    data: topTerms.map((agg) => agg.count),
                },
            ],
        };
    }, [entitiesPerGlossaryTermData, t]);

    return (
        <ChartWidgetCard
            title={t('Entities per Glossary Term')}
            loading={entitiesPerGlossaryTermLoading}
            isEmpty={categories.length === 0}
        >
            <ColumnChart showLegend height={310} series={series} categories={categories} />
        </ChartWidgetCard>
    );
};
